import { format } from 'date-fns'
import { useEffect, useState } from 'react'

import { getFio } from 'helpers/formatText'
import { useMutation } from 'hooks/useMutation'
import { usePagination } from 'hooks/usePagination'
import { useQuery } from 'hooks/useQuery'
import { useGetRegionsQuery } from 'redux/services/common'
import { PayoutApi } from 'services/api/PayoutApi'

import { AppLayoutTable } from 'components/AppLayoutTable'
import { AppLoader } from 'components/AppLoader'
import { AppPagination } from 'components/AppPagination'
import { AppTable } from 'components/AppTable'

import {
  CalculatorType,
  GetAttractorsParamsType,
  PayoutUserType,
} from 'models/payout'

import { AttractorDetails } from './components/AttractorDetails'
import { AttractorDetailsEmpty } from './components/AttractorDetailsEmpty'
import { AttractorDetailsHeader } from './components/AttractorDetailsHeader'

export const AttractorsPage = () => {
  const { data: regions = [] } = useGetRegionsQuery()

  const [mounted, setMounted] = useState(false)
  const [downloadingExcel, setDownloadingExcel] = useState(false)
  const [details, setDetails] = useState<PayoutUserType | null>(null)
  const [calcs, setCalcs] = useState<CalculatorType[]>([])
  const { page, list, count, getList, setList, fetchingList } = usePagination(
    PayoutApi.getAllUsers,
  )
  const withoutCalcs = useQuery<{ count: number }, GetAttractorsParamsType>(
    params => PayoutApi.getUsersCountWithoutCalc(params),
  )
  const { mutate, mutating } = useMutation(PayoutApi.setCalculatorToUser)

  const openDetails = async (payoutUser: PayoutUserType) => {
    try {
      if (payoutUser.phone === details?.phone) {
        return
      }
      closeDetails()
      setDetails(payoutUser)
    } catch (error) {}
  }

  const closeDetails = () => {
    setDetails(null)
  }

  const onSwap = (future_calc: number) => {
    if (details) {
      mutate({
        phone: details?.phone,
        future_calc,
      }).then(({ data }) => {
        const newList = list.map(item =>
          item.phone === data.phone ? data : item,
        )
        setList(newList)
      })
    }
  }

  const applyFilters = (filters: GetAttractorsParamsType) => {
    withoutCalcs.fetchData(filters)
    getList(1, filters)
    closeDetails()
  }

  const downloadExcel = async (filters: GetAttractorsParamsType) => {
    setDownloadingExcel(true)
    try {
      const { data } = await PayoutApi.downloadExcel(filters)
      const filename = `reports_for_${format(new Date(), 'yyyy-MM-dd')}.xlsx`
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)

      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
    } catch (error) {}
    setDownloadingExcel(false)
  }

  const getCalcs = async () => {
    try {
      const { data } = await PayoutApi.getCalcs({ limit: 1000 })
      setCalcs(data.results)
    } catch (error) {}
  }

  const getData = async () => {
    Promise.all([getCalcs()]).finally(() => {
      setMounted(true)
    })
  }

  useEffect(() => {
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) {
    return <AppLoader />
  }

  return (
    <>
      <AppLayoutTable
        title={`Привлеченцы без текущего или след. калькулятора - ${
          withoutCalcs.isSuccess ? withoutCalcs.data.count : '...'
        }`}
        header={
          <AttractorDetailsHeader
            regions={regions}
            calcs={calcs}
            onSubmit={applyFilters}
            downloadExcel={downloadExcel}
            downloadingExcel={downloadingExcel}
          />
        }
        sidebarOpened={Boolean(details)}
        sidebar={
          details ? (
            <AttractorDetails
              user={details}
              disabled={mutating}
              onClose={closeDetails}
              onSwap={onSwap}
            />
          ) : (
            <AttractorDetailsEmpty />
          )
        }
      >
        {' '}
        {fetchingList ? (
          <AppLoader />
        ) : (
          <AppTable
            dataSource={list}
            onClickRow={openDetails}
            checkError={item => !(item.future_calc && item.current_calc)}
            selected={{
              value: details?.phone,
              key: 'phone',
            }}
            headers={[
              {
                key: 'last_name',
                data: 'Ф.И.О',
                render: (_, user) => getFio(user),
              },
              {
                key: 'phone',
                data: 'Номер телефона',
              },
              {
                key: 'last_calc',
                data: 'Предыдущий калькулятор',
                render: (_, item) => item?.last_calc?.calculator?.plan?.name,
              },
              {
                key: 'current_calc',
                data: 'Текущий калькулятор',
                render: (_, item) => item?.current_calc?.calculator?.plan?.name,
              },
              {
                key: 'future_calc',
                data: 'Следующий калькулятор',
                render: (_, item) => item?.future_calc?.calculator?.plan?.name,
              },
            ]}
          />
        )}
      </AppLayoutTable>

      {count > 0 ? (
        <AppPagination
          forcePage={page - 1}
          onPageChange={getList}
          count={count}
        />
      ) : null}
    </>
  )
}
